import m from 'mithril'
import router, { routes } from './router.js'
import { map } from '@shared/components/mapbox'
import HeatMetricStateModel from '@app/models/heat-metric-state'

import "./styles/app.scss";

import Loading from '@app/pages/loading'
import Notifications from '@client-shared/components-v2/toast'
import { getEnvVarAsBoolean } from '@client-shared/utils/env-vars';
import { addRemoveChildDebugger } from '@client-shared/utils/debug';
import { isClientFeatureFlagEnabled } from '@client-shared/utils/feature-flags';
import { FEATURE_FLAGS } from '@client-shared/constants';

const getDefaultExport = module => module.default

const lazyPage = (loader, pickComponent = getDefaultExport) => {
	let Component = null
	let promise = null
	let error = null
	let redraw_scheduled = false

	const load = () => {
		if (Component) return Promise.resolve(Component)
		if (error) return Promise.reject(error)

		promise = promise || loader()
			.then(module => {
				Component = pickComponent(module)
				return Component
			})
			.catch(err => {
				error = err
				throw err
			})

		return promise
	}

	return {
		oninit: load,
		preload: load,
		isLoaded: () => Boolean(Component),
		view: vnode => {
			if (error) throw error
			if (!Component) {
				load()
				if (!redraw_scheduled) {
					redraw_scheduled = true
					promise?.finally(() => {
						redraw_scheduled = false
						m.redraw()
					})
				}
				return m(Loading)
			}

			return m(Component, vnode.attrs)
		},
	}
}

// Assign components to routes
routes.root.component = lazyPage(() => import("@app/pages/root"))
routes.optimus_migrate.component = lazyPage(() => import("@app/pages/optimus-migrate/optimus-migrate"))
routes.system.component = lazyPage(() => import("@app/pages/system"))
routes.sigops_region.component = lazyPage(() => import("@app/pages/sigops-region"))
routes.sigops_subregion.component = lazyPage(() => import("@app/pages/sigops-subregion"))
routes.sigops_network.component = lazyPage(() => import("@app/pages/sigops-network"))
routes.sigops_network_ad_hoc.component = lazyPage(() => import("@app/pages/sigops-network-ad-hoc/sigops-network-ad-hoc"))
routes.sigops_signal.component = lazyPage(() => import("@app/pages/sigops-signal-phase"))
routes.sigops_signal_ad_hoc.component = lazyPage(() => import("@app/pages/sigops-signal-phase-ad-hoc"))
routes.sigops_phase.component = routes.sigops_signal.component
routes.sigops_phase_ad_hoc.component = routes.sigops_signal_ad_hoc.component
routes.safety_region.component = lazyPage(() => import("@app/pages/safety-region"))
routes.safety_subregion.component = lazyPage(() => import("@app/pages/safety-subregion"))
routes.safety_network.component = lazyPage(() => import("@app/pages/safety-network"))
routes.safety_signal.component = lazyPage(() => import("@app/pages/safety-signal-phase"))
routes.safety_phase.component = routes.safety_signal.component
routes.safety_intersection.component = lazyPage(() => import("@app/pages/safety-intersection"))
routes.mobility_region.component = lazyPage(() => import("@app/pages/mobility-region"))
routes.mobility_subregion.component = lazyPage(() => import("@app/pages/mobility-subregion"))
routes.mobility_network.component = lazyPage(() => import("@app/pages/mobility-network"))
routes.roadway_ad_hoc.component = lazyPage(() => import("@app/pages/edge-group-ad-hoc/edge-group-ad-hoc"))
routes.roadway.component = lazyPage(() => import("@app/pages/edge-group-v2"))
routes.network_edit.component = lazyPage(() => import("@app/pages/sigops-network-ad-hoc/sigops-network-ad-hoc"))
routes.roadway_edit.component = lazyPage(() => import("@app/pages/edge-group-ad-hoc/edge-group-ad-hoc"))
routes.merge_networks.component = lazyPage(() => import("@app/pages/merge-networks/merge-networks"))
routes.origin_destination.component = lazyPage(() => import("@app/pages/origin-destination/origin-destination"))
routes.cerberus.component = lazyPage(() => import("@app/pages/cerberus-v2/cerberus-page.tsx"))
routes.data_monitoring_feeds.component = lazyPage(
	() => import("@app/pages/data-monitoring/views/data-feeds"),
	module => module.DataFeedsView,
)
routes.data_monitoring_feeds_customer.component = lazyPage(
	() => import("@app/pages/data-monitoring/views/data-feeds"),
	module => module.DataFeedsCustomerView,
)
routes.data_monitoring_quality.component = lazyPage(
	() => import("@app/pages/data-monitoring/views/data-quality"),
	module => module.DataQualityView,
)
routes.data_monitoring_quality_customer.component = lazyPage(
	() => import("@app/pages/data-monitoring/views/data-quality"),
	module => module.DataQualityCustomerView,
)
routes.reports.component = lazyPage(() => import("@app/pages/reports/views/reports-layout/reports-layout"))
routes.report_origin_destination.component = lazyPage(() => import("@app/pages/report-origin-destination/report-origin-destination"))
routes.access_denied.component = lazyPage(() => import("@app/pages/access-denied"))

// Sandbox (non-production only) — route definition is in router.js, component assigned here
if (process.env.NODE_ENV !== "production") {
	routes.sandbox.component = lazyPage(() => import("@app/pages/sandbox/sandbox.tsx"))
	routes.sandbox_customer.component = routes.sandbox.component
}

const CORE_MAP_ROUTE_KEYS = [
	"system",
	"sigops_region",
	"sigops_subregion",
	"sigops_network",
	"safety_region",
	"safety_subregion",
	"safety_network",
	"mobility_region",
	"mobility_subregion",
	"mobility_network",
]

const preloadRouteComponent = route_key => {
	const component = routes[route_key]?.component
	if (!component?.preload || component.isLoaded?.()) return Promise.resolve()

	return component.preload().catch(err => {
		console.error(`Failed to preload route chunk "${route_key}"`, err)
	})
}

const preloadCoreMapRouteChunks = () => {
	CORE_MAP_ROUTE_KEYS.forEach(preloadRouteComponent)
}

const scheduleCoreMapRoutePreload = () => {
	if (typeof window === "undefined") return

	if ("requestIdleCallback" in window) {
		window.requestIdleCallback(preloadCoreMapRouteChunks, { timeout: 1500 })
		return
	}

	window.setTimeout(preloadCoreMapRouteChunks, 0)
}

const resolveRouteComponent = route => {
	if (!route) return null

	if (route.component) return route.component

	const route_config = routes[route.name] || Object.values(routes).find(item => item.path === route.path)
	return route_config?.component ?? null
}

const RoutingComponent = {}

RoutingComponent.oninit = vnode => {
	router.active = true

	// Initialize heat metric state sync (syncs hmm <-> selected_metric)
	HeatMetricStateModel.init()

	vnode.state.route_key = null
	vnode.state.route = null

	let loading = false

	vnode.state.mapDblClickHandler = () => {
		if (router.disable_active_sidebar_none_dblclick) return;

		router.currentRoute().navigate({ query: { active_sidebar: "none" }, options: { preserve: true } });
		m.redraw();
	};
	map.on("dblclick", vnode.state.mapDblClickHandler);

	vnode.state.onRouteChange = async (route) => {
		const route_key = route.path + JSON.stringify(route.params) + JSON.stringify(route.query);
		const component_key = route.path + JSON.stringify(route.params);

		if (vnode.state.route_key === route_key) return;

		vnode.state.route_key = route_key;
		route.key = component_key;

		// Set the route's <title> value, which can include URL parameter values
		const path_params = {};
		route.matcher.keys.forEach((key) => {
			path_params[key] = route.params[key];
		});
		const title_string = Object.entries(path_params).reduce(
			(t_s, [k, v]) => t_s?.replaceAll(`:${k}`, v),
			route.title,
		);
		document.title = title_string ?? "Dashboard | Flow Labs";

		route.component = resolveRouteComponent(route);
		vnode.state.route = route;

		m.redraw();
	};

	router.onNavigation(async (route) => {
		await vnode.state.onRouteChange(route);
	});

	vnode.state.onRouteChange(router.currentRoute());
};

RoutingComponent.onremove = vnode => {
	map.off('dblclick', vnode.state.mapDblClickHandler)
}

RoutingComponent.view = vnode => {
	const RouteComponent = resolveRouteComponent(vnode.state.route)

	return [
		RouteComponent ? m(RouteComponent, vnode.state.route) : m(Loading, { key: "route-loading" }),
	]
}

// if (import.meta.hot) {
// 	import.meta.hot.accept((new_module) => {
// 		if (!new_module?.routes) return
// 	})
// }

;(async () => {
	// allow development-specific functionality
	if (import.meta.env.MODE === "development") {
		if (getEnvVarAsBoolean('VITE_MITHRIL_DEBUG', false)) {
			addRemoveChildDebugger()
		}

		if (getEnvVarAsBoolean('VITE_MSW_ENABLED', false)) {
			const { startBrowserMocks } = await import("../client-shared/mock-service/browser")
			await startBrowserMocks()
		}
	}

	let notifications_root = document.getElementById("notifications-root")
	if (!notifications_root) {
		notifications_root = document.createElement("div")
		notifications_root.id = "notifications-root"
		document.body.appendChild(notifications_root)
	}

	let app_root = document.getElementById("app-root")
	if (!app_root) {
		app_root = document.createElement("div")
		app_root.id = "app-root"
		app_root.style.height = "100%"
		app_root.style.width = "100%"
		document.body.appendChild(app_root)
	}

	m.mount(notifications_root, {
		view: () => m(Notifications, { key: "notification-testing" }),
	})

	m.route.prefix = ""
	m.route(app_root, "/", {
		"/:path...": RoutingComponent,
	})

	scheduleCoreMapRoutePreload()
})()
